import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, Fragment as _Fragment, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, toDisplayString as _toDisplayString, createBlock as _createBlock, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "verification-sheet__intro"
}
const _hoisted_2 = { class: "verification-sheet__copy" }
const _hoisted_3 = { class: "verification-sheet__phrase" }
const _hoisted_4 = { class: "verification-sheet__phrase-value" }
const _hoisted_5 = {
  key: 2,
  class: "verification-sheet__review"
}
const _hoisted_6 = ["src"]
const _hoisted_7 = {
  key: 1,
  class: "space-y-2"
}
const _hoisted_8 = { class: "h-1 w-full overflow-hidden rounded-full bg-brand-dark-gray" }
const _hoisted_9 = { class: "text-center text-sm text-brand-light-gray" }
const _hoisted_10 = { class: "verification-sheet__actions verification-sheet__actions--split" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { key: 0 }
const _hoisted_14 = {
  key: 1,
  class: "h-5 w-5 animate-spin text-brand-black",
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  viewBox: "0 0 24 24"
}

import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import {IonContent, IonPage, modalController, toastController} from '@ionic/vue';
import ApiService from '@/services/api/apiService';
import {Observable, ObservableEvent} from '@/services/observable/Observable';
import {AccountType} from '@/definitions/AccountType';
import {UserBasicInformationInterface} from '@/interfaces/UserBasicInformationInterface';
import VerificationRecorderView from '@/components/VerificationRecorderView.vue';
import {VERIFICATION_PHRASE} from '@/composables/useVerificationVideoRecorder';

type Phase = 'intro' | 'record' | 'review';


export default /*@__PURE__*/_defineComponent({
  __name: 'VerificationSheet',
  setup(__props) {

const phase = ref<Phase>('intro');
const authUser = ref<UserBasicInformationInterface>();
const recordedBlob = ref<Blob | null>(null);
const reviewUrl = ref<string | null>(null);
const posting = ref(false);
const uploadProgress = ref(0);

const verificationPhrase = VERIFICATION_PHRASE;

const coupleAccount = computed(() => {
  const type = authUser.value?.account_type;
  return type === AccountType.Couple || type === AccountType.Joint;
});

const cleanupReviewUrl = () => {
  if (reviewUrl.value) {
    URL.revokeObjectURL(reviewUrl.value);
    reviewUrl.value = null;
  }
};

const showError = async (message: string) => {
  const toast = await toastController.create({
    message,
    duration: 3500,
    position: 'bottom',
    color: 'danger',
  });
  await toast.present();
};

const beginRecording = () => {
  phase.value = 'record';
};

const handleRecorded = (blob: Blob) => {
  cleanupReviewUrl();
  recordedBlob.value = blob;
  reviewUrl.value = URL.createObjectURL(blob);
  phase.value = 'review';
};

const retake = () => {
  cleanupReviewUrl();
  recordedBlob.value = null;
  phase.value = 'record';
};

const submitVideo = async () => {
  if (!recordedBlob.value || posting.value) {
    return;
  }

  if (recordedBlob.value.size === 0) {
    await showError('Your recording is empty. Please record again.');
    return;
  }

  try {
    posting.value = true;
    uploadProgress.value = 0;

    const mimeType = (recordedBlob.value.type || 'video/webm').split(';')[0];
    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    const file = new File([recordedBlob.value], `verification.${extension}`, {
      type: mimeType,
    });

    await ApiService.User().attachVerificationVideo(file, (progress: number) => {
      uploadProgress.value = parseInt(`${progress}`, 10);
    });

    Observable.emit(ObservableEvent.UserChanged);

    const toast = await toastController.create({
      message: 'Thank you for submitting your verification video. Our team aim to review all verification submissions within 48 hours of submission.',
      duration: 3500,
      position: 'top',
      color: 'primary',
    });
    await toast.present();

    await modalController.dismiss();
  } catch (e: any) {
    const message = e?.data?.message
        || e?.message
        || (Array.isArray(e?.errors?.video) ? e.errors.video[0] : null)
        || 'Unable to upload your verification video. Please try again.';
    await showError(message);
  } finally {
    posting.value = false;
  }
};

onBeforeUnmount(cleanupReviewUrl);

onMounted(async () => {
  authUser.value = await ApiService.User().authUser();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonPage), {
    class: _normalizeClass({ 'verification-modal--recording': phase.value === 'record' })
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(IonContent), {
        fullscreen: true,
        "scroll-y": false,
        class: _normalizeClass({ 'verification-content--transparent': phase.value === 'record' })
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            class: _normalizeClass(["verification-sheet", phase.value === 'record' ? 'verification-sheet--recording' : ''])
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["verification-sheet__content", {
              'verification-sheet__content--wide': phase.value === 'record',
              'verification-sheet__content--review': phase.value === 'review',
            }])
            }, [
              (phase.value === 'intro')
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _cache[8] || (_cache[8] = _createElementVNode("div", { class: "verification-sheet__icon" }, [
                      _createElementVNode("svg", {
                        class: "h-5 fill-current text-brand-black",
                        xmlns: "http://www.w3.org/2000/svg",
                        viewBox: "0 0 448 512"
                      }, [
                        _createElementVNode("path", { d: "M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" })
                      ])
                    ], -1)),
                    _createElementVNode("div", _hoisted_2, [
                      _cache[6] || (_cache[6] = _createElementVNode("h2", { class: "verification-sheet__title" }, "Verification Required", -1)),
                      (!coupleAccount.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _cache[1] || (_cache[1] = _createElementVNode("p", { class: "verification-sheet__text" }, " To ensure the safety of all members of the Pineapple community you must verify that you are who you say you are. Once submitted you will have view only access until your profile is verified by the Pineapple team. ", -1)),
                            _cache[2] || (_cache[2] = _createElementVNode("p", { class: "verification-sheet__text verification-sheet__text--emphasis" }, " You'll record a short video selfie in the app and say the phrase below. ", -1))
                          ], 64))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _cache[3] || (_cache[3] = _createElementVNode("p", { class: "verification-sheet__text" }, [
                              _createTextVNode(" To ensure the safety of all members of the Pineapple community "),
                              _createElementVNode("span", { class: "text-white" }, "you must verify that you are a couple"),
                              _createTextVNode(". Once submitted you will have view only access until your profile is verified by the Pineapple team. ")
                            ], -1)),
                            _cache[4] || (_cache[4] = _createElementVNode("p", { class: "verification-sheet__text verification-sheet__text--emphasis" }, " Record a short video selfie in the app with one of you saying the phrase below. ", -1)),
                            _cache[5] || (_cache[5] = _createElementVNode("div", { class: "verification-sheet__notice" }, " You are verifying as a couple so you both MUST be present in the video or your verification will be changed to a solo account. ", -1))
                          ], 64))
                    ]),
                    _createElementVNode("div", _hoisted_3, [
                      _cache[7] || (_cache[7] = _createElementVNode("div", { class: "verification-sheet__phrase-label" }, "You'll be asked to say", -1)),
                      _createElementVNode("div", _hoisted_4, "\"" + _toDisplayString(_unref(verificationPhrase)) + "\"", 1)
                    ]),
                    _createElementVNode("div", { class: "verification-sheet__actions verification-sheet__actions--single" }, [
                      _createElementVNode("button", {
                        type: "button",
                        onClick: beginRecording,
                        class: "verification-sheet__primary-btn"
                      }, " I'm ready to record ")
                    ]),
                    _cache[9] || (_cache[9] = _createElementVNode("p", { class: "verification-sheet__footnote" }, " Verification videos are removed from our servers upon approval or rejection. ", -1))
                  ]))
                : (phase.value === 'record')
                  ? (_openBlock(), _createBlock(VerificationRecorderView, {
                      key: 1,
                      "couple-account": coupleAccount.value,
                      phrase: _unref(verificationPhrase),
                      onCancel: _cache[0] || (_cache[0] = ($event: any) => (phase.value = 'intro')),
                      onRecorded: handleRecorded,
                      onError: showError
                    }, null, 8, ["couple-account", "phrase"]))
                  : (phase.value === 'review')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        _cache[11] || (_cache[11] = _createElementVNode("div", { class: "verification-sheet__copy" }, [
                          _createElementVNode("h2", { class: "verification-sheet__title" }, "Review your video"),
                          _createElementVNode("p", { class: "verification-sheet__text" }, "Make sure you're clearly visible and the phrase is easy to hear.")
                        ], -1)),
                        (reviewUrl.value)
                          ? (_openBlock(), _createElementBlock("video", {
                              key: 0,
                              src: reviewUrl.value,
                              class: "verification-sheet__video",
                              controls: "",
                              playsinline: ""
                            }, null, 8, _hoisted_6))
                          : _createCommentVNode("", true),
                        (uploadProgress.value > 0 && posting.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                              _createElementVNode("div", _hoisted_8, [
                                _createElementVNode("div", {
                                  class: "h-full bg-brand-lime transition-all",
                                  style: _normalizeStyle({ width: `${uploadProgress.value}%` })
                                }, null, 4)
                              ]),
                              _createElementVNode("div", _hoisted_9, "Uploading… " + _toDisplayString(uploadProgress.value) + "%", 1)
                            ]))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("button", {
                            type: "button",
                            onClick: retake,
                            disabled: posting.value,
                            class: "verification-sheet__secondary-btn"
                          }, " Retake ", 8, _hoisted_11),
                          _createElementVNode("button", {
                            type: "button",
                            onClick: submitVideo,
                            disabled: posting.value || !recordedBlob.value,
                            class: "verification-sheet__primary-btn verification-sheet__primary-btn--compact"
                          }, [
                            (!posting.value)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_13, "Submit"))
                              : (_openBlock(), _createElementBlock("svg", _hoisted_14, [...(_cache[10] || (_cache[10] = [
                                  _createElementVNode("circle", {
                                    class: "opacity-25",
                                    cx: "12",
                                    cy: "12",
                                    r: "10",
                                    stroke: "currentColor",
                                    "stroke-width": "4"
                                  }, null, -1),
                                  _createElementVNode("path", {
                                    class: "opacity-75",
                                    fill: "currentColor",
                                    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  }, null, -1)
                                ]))]))
                          ], 8, _hoisted_12)
                        ])
                      ]))
                    : _createCommentVNode("", true)
            ], 2)
          ], 2)
        ]),
        _: 1
      }, 8, ["class"])
    ]),
    _: 1
  }, 8, ["class"]))
}
}

})